import {
    decodeSymbols,
    getNodesForTranslation,
    getSentences,
    getSentenceText,
    type ITranslateElements,
    pluralize,
    pluralizeHtml,
    replaceOldText,
} from "./translatorTools";

interface ITranslateWaiting {
    sentences: string[];
    resolve: (res: ITranslateResult) => void;
}

export interface ITranslateResult {
    original: string[];
    translation: string[];
    dict: Map<string, string>;
}

export interface ITranslator {
    translateHtml: (elemsArray: HTMLElement[]) => Promise<ITranslateResult>;
    translateSentences: (sentences: string[]) => Promise<ITranslateResult>;
    translate: (sentences: string[]) => Promise<ITranslateResult>;
    pluralize: (sentence: string, ...values: number[]) => string;
}

export interface ITranslateRequest {
    language: string;
    sentences: string[];
}

interface IMissingTranslations {
    existing: [string, string][];
    missing: string[];
}

/**
 * Get missing translations from the provided list.
 * For non-English languages, all translations are considered missing.
 * If the language is English, only translations with the specified key prefix are considered missing.
 * If key is not provided, all translations are considered existing for English (we assume the key is the same as value).
 * @param translations - Array of translation strings to check.
 * @param language - Target language code.
 * @param keyPrefix - Optional prefix to identify translation keys.
 * @returns An object containing existing translations and missing translations.
 */
export const getMissingTranslations = (
    translations: string[],
    language: string,
    keyPrefix: string | undefined = undefined
): IMissingTranslations => {
    if (language !== "en") {
        return { existing: [], missing: translations };
    }

    if (keyPrefix === undefined) {
        return { existing: translations.map((t) => [t, t]), missing: [] };
    }

    return translations.reduce(
        (acc, t) => {
            if (t.startsWith(keyPrefix)) {
                acc.missing.push(t);
                return acc;
            }

            acc.existing.push([t, t]);
            return acc;
        },
        { existing: [], missing: [] } as IMissingTranslations
    );
};

/**
 * Get HTML elements that require translation based on the specified language and optional key prefix.
 * For non-English languages, all elements with translatable text are included.
 * For English, only elements with text starting with the specified key prefix are included.
 * If no key prefix is provided for English, no elements are included.
 * @param elements - Array of HTML elements to check.
 * @param language - Target language code.
 * @param keyPrefix - Optional prefix to identify translation keys.
 * @returns A tuple containing an array of elements where translations are taken from and an array of corresponding sentences.
 */
export const getHtmlElementsToTranslate = (
    elements: HTMLElement[],
    language: string,
    keyPrefix: string | undefined = undefined
): [ITranslateElements[], string[]] => {
    if (language !== "en") {
        let groupsForTranslation = getNodesForTranslation(elements);
        return [groupsForTranslation, getSentences(groupsForTranslation)];
    }

    if (keyPrefix === undefined) {
        return [[], []];
    }

    let groupsForTranslation = getNodesForTranslation(elements);
    return groupsForTranslation.reduce(
        (akk, element) => {
            const text = getSentenceText(element);
            if (text.startsWith(keyPrefix)) {
                akk[0].push(element);
                akk[1].push(text);
            }
            return akk;
        },
        [[], []] as [ITranslateElements[], string[]]
    );
};

const mergeTranslations = (
    original: string[],
    transl: string[]
): [string, string][] =>
    original.map((orig, index) => [orig, transl[index] || orig]);

export function Translator(
    language: string,
    apiTransport: (
        action: string,
        data: { language: string; sentences: string[] }
    ) => Promise<string[]>,
    keyPrefix: string | undefined = undefined
): ITranslator {
    let translateWaiting: ITranslateWaiting[] = [],
        genResult = (
            original: string[],
            transl: string[]
        ): ITranslateResult => ({
            original,
            translation: transl.length > 0 ? transl : original,
            dict: new Map(mergeTranslations(original, transl)),
        }),
        worker = function () {
            const currentTranslation = translateWaiting.slice();
            const sentences = currentTranslation.reduce(
                (result, item) => result.concat(item.sentences),
                [] as string[]
            );
            const { missing, existing } = getMissingTranslations(
                sentences,
                language,
                keyPrefix
            );
            const sentencesUnique = new Set(missing);
            const sentencesUniqueArray = Array.from(sentencesUnique);
            const resolveTranslationTask = function (response: string[]) {
                const dict = new Map([
                    ...existing,
                    ...mergeTranslations(sentencesUniqueArray, response),
                ]);
                const translated = sentences.map((s) => dict.get(s) || s);

                let start = 0;
                for (let i = 0; i < currentTranslation.length; ++i) {
                    let item = currentTranslation[i],
                        part = translated
                            .slice(start, start + item.sentences.length)
                            .map(decodeSymbols),
                        result = genResult(item.sentences, part);

                    item.resolve(result);
                    start += item.sentences.length;
                }
            };

            translateWaiting.length = 0;

            if (sentencesUniqueArray.length === 0) {
                resolveTranslationTask(sentencesUniqueArray);
                return;
            }

            apiTransport("Translate", {
                sentences: sentencesUniqueArray,
                language,
            }).then(resolveTranslationTask, () =>
                resolveTranslationTask(sentencesUniqueArray)
            );
        },
        translate = function (sentences: string[]) {
            if (translateWaiting.length === 0) {
                setTimeout(worker, 1);
            }

            return new Promise<ITranslateResult>((resolve) => {
                translateWaiting.push({
                    sentences,
                    resolve,
                });
            });
        };

    return {
        translateHtml: function (elemsArray: HTMLElement[]) {
            let [groupsForTranslation, sentences] = getHtmlElementsToTranslate(
                elemsArray,
                language,
                keyPrefix
            );

            if (sentences.length > 0) {
                return translate(sentences).then((result) => {
                    replaceOldText(groupsForTranslation, result.translation);
                    pluralizeHtml(language, elemsArray);
                    return result;
                });
            }

            return Promise.resolve(genResult([], []));
        },
        translateSentences: (sentences: string[]) => translate(sentences),
        translate,
        pluralize: (sentence: string, ...values: number[]) =>
            pluralize(language, sentence, ...values),
    };
}

